"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useBrand } from "~/config/brands";
import {
  AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  AGENT_SIDEBAR_OPEN_EVENT,
  isAgentProfileRoute,
  isBuilderEmbedContext,
  isSiteGlueOnboardRoute,
  readStoredSidebarWidthPx,
  resolveConciergeProjectId,
  shouldShowSiteAgentSidebar,
  sidebarStorageKey,
  type AgentSidebarOpenDetail,
} from "~/lib/marketplace/site-agent-sidebar";
import { cn } from "~/lib/utils";
import { LandingAgentSidebar, type SidebarMode } from "./landing-agent-sidebar";

type SiteAgentSidebarLayoutProps = {
  children: React.ReactNode;
};

function readInitialSidebarMode(
  _storageKey: string,
  isAgentProfile: boolean,
  _pathname: string,
): SidebarMode {
  if (isBuilderEmbedContext()) return "closed";
  if (isAgentProfile) return "closed";
  // FAB + CTAs only on load — user opens full widget explicitly
  return "closed";
}

function getServerSafeSidebarMode(pathname: string): SidebarMode {
  if (!shouldShowSiteAgentSidebar(pathname)) return "closed";
  if (isAgentProfileRoute(pathname)) return "closed";
  return "closed";
}

function useDesktopLayout(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [breakpoint]);

  return isDesktop;
}

export function SiteAgentSidebarLayout({ children }: SiteAgentSidebarLayoutProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const brand = useBrand();
  const isDesktop = useDesktopLayout();
  const [mode, setMode] = useState<SidebarMode>(() => getServerSafeSidebarMode(pathname));
  const [sidebarWidthPx, setSidebarWidthPx] = useState(
    AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX,
  );
  /** Marketing Live Demo may temporarily switch away from the site concierge. */
  const [demoProjectId, setDemoProjectId] = useState<string | null>(null);
  const [pendingOpenDetail, setPendingOpenDetail] =
    useState<AgentSidebarOpenDetail | null>(null);

  const defaultProjectId = useMemo(() => resolveConciergeProjectId(pathname), [pathname]);
  const isAgentProfile = isAgentProfileRoute(pathname);
  const projectId =
    !isAgentProfile && demoProjectId ? demoProjectId : defaultProjectId;
  const isBuilderEmbed = searchParams.get("embed") === "builder";
  const isProfileEmbed = searchParams.get("embed") === "1";
  const isMarketingHome = pathname === "/";
  const isOnboard = isSiteGlueOnboardRoute(pathname);
  /** FAB overlay — don't push main content (home + /onboard full-bleed shells). */
  const overlaySidebar = isMarketingHome || isOnboard;
  const showSidebar =
    (brand.id === "easygpt" || brand.id === "siteglue") &&
    !!projectId &&
    shouldShowSiteAgentSidebar(pathname) &&
    !isBuilderEmbed &&
    !isProfileEmbed;
  const storageKey = projectId ? sidebarStorageKey(projectId) : null;

  useEffect(() => {
    setDemoProjectId(null);
    setPendingOpenDetail(null);
  }, [pathname]);

  useEffect(() => {
    if (isAgentProfile) return;

    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<AgentSidebarOpenDetail>).detail;
      if (!detail?.projectId || !detail.allowProjectSwitch) return;
      if (detail.projectId === defaultProjectId) {
        setDemoProjectId(null);
        return;
      }
      setDemoProjectId(detail.projectId);
      setPendingOpenDetail(detail);
      setMode("expanded");
    };

    window.addEventListener(AGENT_SIDEBAR_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(AGENT_SIDEBAR_OPEN_EVENT, handleOpen);
  }, [defaultProjectId, isAgentProfile]);

  /** After switching project, re-dispatch so the new sidebar instance opens the tab. */
  useEffect(() => {
    if (!pendingOpenDetail?.projectId || pendingOpenDetail.projectId !== projectId) {
      return;
    }
    const detail = pendingOpenDetail;
    setPendingOpenDetail(null);
    const timer = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent<AgentSidebarOpenDetail>(AGENT_SIDEBAR_OPEN_EVENT, {
          detail: {
            projectId: detail.projectId,
            tab: detail.tab,
            message: detail.message,
          },
        }),
      );
    }, 50);
    return () => window.clearTimeout(timer);
  }, [projectId, pendingOpenDetail]);

  useEffect(() => {
    if (!storageKey) return;
    // Don't collapse an in-progress Live Demo project switch.
    if (demoProjectId && projectId === demoProjectId) return;
    setMode(readInitialSidebarMode(storageKey, isAgentProfile, pathname));
  }, [storageKey, isAgentProfile, pathname, demoProjectId, projectId]);

  useEffect(() => {
    if (!projectId) return;
    const stored = readStoredSidebarWidthPx(projectId);
    if (stored) setSidebarWidthPx(stored);
  }, [projectId]);

  const handleModeChange = (nextMode: SidebarMode) => {
    setMode(nextMode);
    if (storageKey && !isBuilderEmbedContext()) {
      window.sessionStorage.setItem(storageKey, nextMode);
    }
  };

  if (!showSidebar || !projectId) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      <div
        className={cn("transition-[margin-right] duration-300")}
        style={
          isDesktop && mode !== "closed" && !overlaySidebar
            ? { marginRight: `${sidebarWidthPx}px` }
            : undefined
        }
      >
        {children}
      </div>
      <LandingAgentSidebar
        key={projectId}
        projectId={projectId}
        layout={isAgentProfile ? "agent-profile" : "site"}
        mode={mode}
        onModeChange={handleModeChange}
        sidebarWidthPx={sidebarWidthPx}
        onSidebarWidthChange={setSidebarWidthPx}
      />
    </div>
  );
}
